import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { BookOpen, Plus, Edit, Trash2, Search, Eye, X, Download, HelpCircle, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useSubscription } from "@/context/SubscriptionContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Sales", "Purchase", "Credit Note", "Debit Note"];
const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu and Kashmir","Ladakh",
  "Puducherry","Chandigarh","Dadra and Nagar Haveli","Daman and Diu","Lakshadweep","Andaman and Nicobar"
];

const emptySalesItem = { item_id: "", item_name: "", description: "", hsn_sac: "", qty: "", rate: "", amount: "" };
const emptyPurchaseItem = { material_id: "", material_code: "", product_type: "", item_type: "raw_material", description: "", hsn_sac: "", qty: "", rate: "", amount: "", reel_id: "", reel_size: "", reel_weight: "", consumption_category: "" };

const AccountsVouchers = () => {
  const [vouchers, setVouchers] = useState([]);
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [filterCat, setFilterCat] = useState("All");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showTallyHelp, setShowTallyHelp] = useState(false);
  const { hasFeature } = useSubscription();

  // Reference data
  const [productSpecs, setProductSpecs] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [mills, setMills] = useState([]);
  const [dealers, setDealers] = useState([]);

  // Form state
  const [fCat, setFCat] = useState("Sales");
  const [fVoucherType, setFVoucherType] = useState("");
  const [fInvoice, setFInvoice] = useState("");
  const [fInvoiceSettingId, setFInvoiceSettingId] = useState("");
  const [fDate, setFDate] = useState(new Date().toISOString().split("T")[0]);
  const [fPartyId, setFPartyId] = useState("");
  const [fPlaceOfSupply, setFPlaceOfSupply] = useState("");
  const [fMillName, setFMillName] = useState("");
  const [fDealerName, setFDealerName] = useState("");
  const [fItems, setFItems] = useState([{...emptySalesItem}]);
  const [fTaxRate, setFTaxRate] = useState("");
  const [fRoundOff, setFRoundOff] = useState("0");
  const [fNarration, setFNarration] = useState("");

  const fetchAll = useCallback(async () => {
    const [vRes, vtRes, cRes, vendRes, trRes] = await Promise.all([
      axios.get(`${API}/accounts/vouchers`),
      axios.get(`${API}/accounts/voucher-types`),
      axios.get(`${API}/accounts/customers`),
      axios.get(`${API}/accounts/vendors`),
      axios.get(`${API}/accounts/tax-rates`),
    ]);
    setVouchers(vRes.data);
    setVoucherTypes(vtRes.data);
    setCustomers(cRes.data);
    setVendors(vendRes.data);
    setTaxRates(trRes.data);

    // Fetch reference data for line items
    try {
      const [specRes, matRes] = await Promise.all([
        axios.get(`${API}/accounts/product-specs`),
        axios.get(`${API}/accounts/materials-for-purchase`),
      ]);
      setProductSpecs(specRes.data.specs || []);
      setHsnCodes(specRes.data.hsn_codes || []);
      setMaterials(matRes.data.materials || []);
      setMills(matRes.data.mills || []);
      setDealers(matRes.data.dealers || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-fetch invoice number when category changes
  const fetchNextInvoice = useCallback(async (cat) => {
    try {
      const r = await axios.get(`${API}/accounts/next-invoice-number/${cat}`);
      if (r.data.has_setting) {
        setFInvoice(r.data.invoice_number);
        setFInvoiceSettingId(r.data.setting_id);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (showForm && !editing) fetchNextInvoice(fCat);
  }, [fCat, showForm, editing, fetchNextInvoice]);

  const isSalesType = fCat === "Sales" || fCat === "Credit Note";

  // Compute totals
  const subtotal = fItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const taxRate = parseFloat(fTaxRate) || 0;
  const selectedParty = isSalesType
    ? customers.find(c => c.id === fPartyId)
    : vendors.find(v => v.id === fPartyId);
  const companyState = "Maharashtra";
  const isInterState = fPlaceOfSupply && fPlaceOfSupply !== companyState;
  const taxAmount = subtotal * taxRate / 100;
  const cgst = isInterState ? 0 : taxAmount / 2;
  const sgst = isInterState ? 0 : taxAmount / 2;
  const igst = isInterState ? taxAmount : 0;
  const roundOff = parseFloat(fRoundOff) || 0;
  const total = subtotal + taxAmount + roundOff;

  const parties = isSalesType ? customers : vendors;
  const partyLabel = isSalesType ? "Customer" : "Vendor";
  const filteredVTypes = voucherTypes.filter(vt => vt.category === fCat);

  const updateSalesItem = (idx, field, val) => {
    setFItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = {...item, [field]: val};
      if (field === "qty" || field === "rate") {
        updated.amount = String((parseFloat(updated.qty) || 0) * (parseFloat(updated.rate) || 0));
      }
      return updated;
    }));
  };

  const selectSalesProduct = (idx, spec) => {
    setFItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const defaultHsn = hsnCodes.find(h => h.code === "48191010")?.code || "48191010";
      return { ...item, item_id: spec.id, item_name: spec.item_name, hsn_sac: defaultHsn, rate: String(spec.rate || ""), amount: String((parseFloat(item.qty) || 0) * (spec.rate || 0)) };
    }));
  };

  const selectPurchaseMaterial = (idx, mat) => {
    setFItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      return { ...item, material_id: mat.id, material_code: mat.product_code, product_type: mat.product_type || "", hsn_sac: mat.hsn_code || "", rate: String(mat.buying_price || ""), reel_size: String(mat.reel_size_inches || ""), item_type: "raw_material", amount: String((parseFloat(item.qty) || 0) * (mat.buying_price || 0)) };
    }));
  };

  const resetForm = () => {
    setFCat("Sales"); setFVoucherType(""); setFInvoice(""); setFInvoiceSettingId("");
    setFDate(new Date().toISOString().split("T")[0]);
    setFPartyId(""); setFPlaceOfSupply(""); setFMillName(""); setFDealerName("");
    setFItems([{...emptySalesItem}]); setFTaxRate(""); setFRoundOff("0"); setFNarration("");
    setEditing(null);
  };

  const handleSave = async () => {
    if (!fDate || !fPartyId) { toast.error("Date and party are required"); return; }
    const partyName = isSalesType
      ? customers.find(c => c.id === fPartyId)?.name
      : vendors.find(v => v.id === fPartyId)?.company_name;
    const vtName = voucherTypes.find(v => v.id === fVoucherType)?.name || "";

    const payload = {
      category: fCat, voucher_type_id: fVoucherType, voucher_type_name: vtName,
      invoice_number: fInvoice, date: fDate,
      party_id: fPartyId, party_name: partyName || "",
      party_gst: selectedParty?.gst_number || selectedParty?.gstin || "",
      party_address: selectedParty?.address || "",
      party_state: selectedParty?.state || "",
      place_of_supply: fPlaceOfSupply,
      mill_name: fMillName, dealer_name: fDealerName,
      items: fItems.filter(i => i.item_name || i.material_code || i.description),
      subtotal, cgst_amount: cgst, sgst_amount: sgst, igst_amount: igst,
      tax_rate: taxRate, round_off: roundOff, total, narration: fNarration,
    };

    try {
      if (editing) {
        await axios.put(`${API}/accounts/vouchers/${editing.id}`, payload);
        toast.success("Voucher updated");
      } else {
        const res = await axios.post(`${API}/accounts/vouchers`, payload);
        const invCreated = res.data?.inventory_created || [];
        if (invCreated.length > 0) {
          toast.success(`Voucher saved + ${invCreated.length} inventory entries created`);
        } else {
          toast.success("Voucher created");
        }
        // Increment invoice number
        if (fInvoiceSettingId) {
          try { await axios.post(`${API}/accounts/increment-invoice-number/${fInvoiceSettingId}`); } catch { /* ignore */ }
        }
      }
      setShowForm(false); resetForm(); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this voucher?")) return;
    await axios.delete(`${API}/accounts/vouchers/${id}`);
    toast.success("Deleted"); fetchAll();
  };

  const handleExportTallyXML = async () => {
    const exportIds = filtered.map(v => v.id);
    if (exportIds.length === 0) { toast.error("No vouchers to export"); return; }
    setExporting(true);
    try {
      const payload = filterCat !== "All" ? { category: filterCat } : { voucher_ids: exportIds };
      const res = await axios.post(`${API}/accounts/vouchers/export-tally-xml`, payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `tally_vouchers_${filterCat === "All" ? "all" : filterCat.replace(/\s/g, "_")}_${new Date().toISOString().split("T")[0]}.xml`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported ${exportIds.length} vouchers`);
    } catch (err) { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const openEdit = (v) => {
    setEditing(v); setFCat(v.category); setFVoucherType(v.voucher_type_id || "");
    setFInvoice(v.invoice_number || ""); setFDate(v.date || ""); setFPartyId(v.party_id || "");
    setFPlaceOfSupply(v.place_of_supply || ""); setFMillName(v.mill_name || ""); setFDealerName(v.dealer_name || "");
    setFItems(v.items?.length ? v.items : (v.category === "Purchase" ? [{...emptyPurchaseItem}] : [{...emptySalesItem}]));
    setFTaxRate(String(v.tax_rate || "")); setFRoundOff(String(v.round_off || "0")); setFNarration(v.narration || "");
    setShowForm(true);
  };

  const filtered = vouchers.filter(v => {
    if (filterCat !== "All" && v.category !== filterCat) return false;
    if (search) {
      const q = search.toLowerCase();
      return v.party_name?.toLowerCase().includes(q) || v.invoice_number?.toLowerCase().includes(q) || v.voucher_type_name?.toLowerCase().includes(q);
    }
    return true;
  });

  const catColor = (c) => ({ "Sales": "text-green-400 bg-green-900/30", "Purchase": "text-blue-400 bg-blue-900/30", "Credit Note": "text-amber-400 bg-amber-900/30", "Debit Note": "text-red-400 bg-red-900/30" }[c] || "text-gray-400");

  // Searchable item dropdown component for Sales
  const SalesItemPicker = ({ idx, item }) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const filteredSpecs = productSpecs.filter(s =>
      !q || s.item_name?.toLowerCase().includes(q.toLowerCase()) || s.company_name?.toLowerCase().includes(q.toLowerCase())
    ).slice(0, 30);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full h-9 justify-between bg-gray-800 border-gray-600 text-white text-xs px-2 truncate">
            <span className="truncate">{item.item_name || "Select product..."}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0 bg-gray-900 border-gray-600" align="start">
          <Command className="bg-gray-900" shouldFilter={false}>
            <CommandInput placeholder="Search product..." value={q} onValueChange={setQ} className="bg-gray-900 text-white h-9" />
            <CommandList className="max-h-48">
              <CommandEmpty>No products found.</CommandEmpty>
              <CommandGroup>
                {filteredSpecs.map(s => (
                  <CommandItem key={s.id} onSelect={() => { selectSalesProduct(idx, s); setOpen(false); setQ(""); }} className="text-white hover:bg-blue-500/20 cursor-pointer text-xs">
                    <Check className={cn("mr-1 h-3 w-3", item.item_id === s.id ? "opacity-100" : "opacity-0")} />
                    <div className="truncate"><span>{s.item_name}</span>{s.company_name && <span className="text-gray-400 ml-1">({s.company_name})</span>}</div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  };

  // Searchable item dropdown for Purchase
  const PurchaseItemPicker = ({ idx, item }) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const filteredMats = materials.filter(m =>
      !q || m.product_code?.toLowerCase().includes(q.toLowerCase()) || m.product_type?.toLowerCase().includes(q.toLowerCase())
    );

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full h-9 justify-between bg-gray-800 border-gray-600 text-white text-xs px-2 truncate">
            <span className="truncate">{item.material_code || "Select material..."}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0 bg-gray-900 border-gray-600" align="start">
          <Command className="bg-gray-900" shouldFilter={false}>
            <CommandInput placeholder="Search material..." value={q} onValueChange={setQ} className="bg-gray-900 text-white h-9" />
            <CommandList className="max-h-48">
              <CommandEmpty>No materials found.</CommandEmpty>
              <CommandGroup>
                {filteredMats.map(m => (
                  <CommandItem key={m.id} onSelect={() => { selectPurchaseMaterial(idx, m); setOpen(false); setQ(""); }} className="text-white hover:bg-blue-500/20 cursor-pointer text-xs">
                    <Check className={cn("mr-1 h-3 w-3", item.material_id === m.id ? "opacity-100" : "opacity-0")} />
                    <span>{m.product_code}</span>
                    <span className="text-gray-400 ml-1">({m.product_type})</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <div className="space-y-4" data-testid="accounts-vouchers-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-blue-400 flex items-center gap-2"><BookOpen className="w-5 h-5" /> Vouchers</h2>
          <p className="text-gray-400 text-sm">Sales, Purchase, Credit & Debit Notes</p>
        </div>
        <div className="flex gap-2">
          {hasFeature("accounts_tally_export") && (
            <>
              <Button size="sm" variant="outline" onClick={() => setShowTallyHelp(true)} className="border-gray-600 text-gray-400 hover:text-white" data-testid="tally-help-btn">
                <HelpCircle className="w-4 h-4 mr-1" /> Tally Guide
              </Button>
              <Button size="sm" variant="outline" onClick={handleExportTallyXML} disabled={exporting || filtered.length === 0}
                className="border-emerald-600 text-emerald-300 hover:bg-emerald-900/30" data-testid="export-tally-xml-btn">
                <Download className="w-4 h-4 mr-1" /> {exporting ? "Exporting..." : "Export XML"}
              </Button>
            </>
          )}
          <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-voucher-btn">
            <Plus className="w-4 h-4 mr-1" /> New Voucher
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="flex gap-1">
          {["All", ...CATEGORIES].map(cat => (
            <Button key={cat} size="sm" variant={filterCat === cat ? "default" : "outline"} onClick={() => setFilterCat(cat)}
              className={filterCat === cat ? "bg-blue-700 text-white" : "border-gray-600 text-gray-400 text-xs"}>{cat}</Button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="bg-gray-800 border-gray-600 text-white pl-9 h-9 w-52" />
        </div>
      </div>

      {/* Voucher Table */}
      <Card className="bg-gray-900/30 border-gray-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700">
                <TableHead className="text-gray-400">Date</TableHead>
                <TableHead className="text-gray-400">Category</TableHead>
                <TableHead className="text-gray-400">Type</TableHead>
                <TableHead className="text-gray-400">Invoice #</TableHead>
                <TableHead className="text-gray-400">Party</TableHead>
                <TableHead className="text-gray-400 text-right">Total</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-8">No vouchers.</TableCell></TableRow>
              ) : filtered.map(v => (
                <TableRow key={v.id} className="border-gray-800">
                  <TableCell className="text-gray-300 whitespace-nowrap">{v.date}</TableCell>
                  <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${catColor(v.category)}`}>{v.category}</span></TableCell>
                  <TableCell className="text-gray-300 text-sm">{v.voucher_type_name || "—"}</TableCell>
                  <TableCell className="text-cyan-400 font-mono text-sm">{v.invoice_number || "—"}</TableCell>
                  <TableCell className="text-white">{v.party_name}</TableCell>
                  <TableCell className="text-right text-emerald-400 font-mono">{new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(v.total || 0)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8 px-2" onClick={() => setShowDetail(v)}><Eye className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8 px-2" onClick={() => openEdit(v)}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 px-2" onClick={() => handleDelete(v.id)}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      {showDetail && (
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-blue-400">{showDetail.category} — {showDetail.invoice_number || "N/A"}</DialogTitle></DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4 bg-gray-800/50 p-4 rounded-lg">
                <div><span className="text-gray-400">Party:</span> <span className="text-white font-medium ml-1">{showDetail.party_name}</span></div>
                <div><span className="text-gray-400">Date:</span> <span className="text-white ml-1">{showDetail.date}</span></div>
                <div><span className="text-gray-400">GST:</span> <span className="text-cyan-400 font-mono ml-1">{showDetail.party_gst || "—"}</span></div>
                <div><span className="text-gray-400">State:</span> <span className="text-white ml-1">{showDetail.party_state || "—"}</span></div>
              </div>
              {showDetail.items?.length > 0 && (
                <Table>
                  <TableHeader><TableRow className="border-gray-700">
                    <TableHead className="text-gray-400">Item</TableHead>
                    <TableHead className="text-gray-400">HSN</TableHead>
                    <TableHead className="text-gray-400 text-right">Qty</TableHead>
                    <TableHead className="text-gray-400 text-right">Rate</TableHead>
                    <TableHead className="text-gray-400 text-right">Amount</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {showDetail.items.map((it, i) => (
                      <TableRow key={i} className="border-gray-800">
                        <TableCell className="text-white">{it.item_name || it.material_code || it.description}</TableCell>
                        <TableCell className="text-gray-300">{it.hsn_sac || "—"}</TableCell>
                        <TableCell className="text-right text-gray-300">{it.qty}</TableCell>
                        <TableCell className="text-right text-gray-300">{it.rate}</TableCell>
                        <TableCell className="text-right text-emerald-400 font-mono">{parseFloat(it.amount || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <div className="bg-gray-800/50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between"><span className="text-gray-400">Subtotal</span><span className="text-white font-mono">{(showDetail.subtotal || 0).toFixed(2)}</span></div>
                {showDetail.cgst_amount > 0 && <div className="flex justify-between"><span className="text-gray-400">CGST</span><span className="text-white font-mono">{showDetail.cgst_amount.toFixed(2)}</span></div>}
                {showDetail.sgst_amount > 0 && <div className="flex justify-between"><span className="text-gray-400">SGST</span><span className="text-white font-mono">{showDetail.sgst_amount.toFixed(2)}</span></div>}
                {showDetail.igst_amount > 0 && <div className="flex justify-between"><span className="text-gray-400">IGST</span><span className="text-white font-mono">{showDetail.igst_amount.toFixed(2)}</span></div>}
                <div className="flex justify-between border-t border-gray-600 pt-2 text-lg"><span className="text-blue-400 font-bold">Total</span><span className="text-emerald-400 font-bold font-mono">{new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(showDetail.total || 0)}</span></div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create/Edit Form */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) { setShowForm(false); resetForm(); } }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-blue-400">{editing ? "Edit" : "New"} Voucher</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Row 1 */}
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-gray-300">Category</Label>
                <Select value={fCat} onValueChange={v => { setFCat(v); setFVoucherType(""); setFPartyId(""); setFItems(v === "Purchase" || v === "Debit Note" ? [{...emptyPurchaseItem}] : [{...emptySalesItem}]); }} disabled={!!editing}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">{CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Voucher Type</Label>
                <Select value={fVoucherType} onValueChange={setFVoucherType}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">{filteredVTypes.map(vt => <SelectItem key={vt.id} value={vt.id} className="text-white">{vt.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Date *</Label>
                <Input type="date" value={fDate} onChange={e => setFDate(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1 [color-scheme:dark]" />
              </div>
              <div>
                <Label className="text-gray-300">Invoice No (Auto)</Label>
                <Input value={fInvoice} onChange={e => setFInvoice(e.target.value)} className="bg-gray-800 border-gray-600 text-cyan-400 font-mono mt-1" />
              </div>
            </div>

            {/* Row 2: Party + Place of Supply */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">{partyLabel} *</Label>
                <Select value={fPartyId} onValueChange={v => { setFPartyId(v); const p = parties.find(pp => pp.id === v); if (p) setFPlaceOfSupply(p.state || ""); }}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue placeholder={`Select ${partyLabel}...`} /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600 max-h-60">{parties.map(p => <SelectItem key={p.id} value={p.id} className="text-white">{p.name || p.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Place of Supply</Label>
                <Select value={fPlaceOfSupply} onValueChange={setFPlaceOfSupply}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue placeholder="Select state..." /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600 max-h-60">{INDIAN_STATES.map(s => <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Party Details Card */}
            {selectedParty && (
              <Card className="bg-blue-950/30 border-blue-500/30" data-testid="party-details-card">
                <CardContent className="p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div><span className="text-gray-400">Name:</span> <span className="text-white font-medium ml-1">{selectedParty.name || selectedParty.company_name}</span></div>
                  <div><span className="text-gray-400">GST:</span> <span className="text-cyan-400 font-mono ml-1">{selectedParty.gst_number || selectedParty.gstin || "—"}</span></div>
                  <div><span className="text-gray-400">State:</span> <span className="text-white ml-1">{selectedParty.state || "—"}</span></div>
                  <div><span className="text-gray-400">Phone:</span> <span className="text-white ml-1">{selectedParty.phone || "—"}</span></div>
                  <div className="col-span-2"><span className="text-gray-400">Address:</span> <span className="text-white ml-1">{selectedParty.address || "—"}</span></div>
                </CardContent>
              </Card>
            )}

            {/* Purchase: Mill & Dealer */}
            {!isSalesType && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-300">Mill Name</Label>
                  <Select value={fMillName} onValueChange={setFMillName}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue placeholder="Select mill..." /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600">{mills.map(m => <SelectItem key={m.id} value={m.name} className="text-white">{m.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Dealer Name</Label>
                  <Select value={fDealerName} onValueChange={setFDealerName}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue placeholder="Select dealer..." /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600">{dealers.map(d => <SelectItem key={d.id} value={d.company_name} className="text-white">{d.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Line Items - SALES */}
            {isSalesType && (
              <div>
                <Label className="text-gray-300 mb-2 block">Line Items (from Product Specifications)</Label>
                <div className="space-y-2">
                  {fItems.map((item, idx) => (
                    <div key={idx} className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-3 space-y-2">
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-4">
                          {idx === 0 && <span className="text-gray-500 text-xs">Product</span>}
                          <SalesItemPicker idx={idx} item={item} />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <span className="text-gray-500 text-xs">HSN/SAC</span>}
                          <Input value={item.hsn_sac || ""} onChange={e => updateSalesItem(idx, "hsn_sac", e.target.value)} className="bg-gray-800 border-gray-600 text-white h-9 text-xs" />
                        </div>
                        <div className="col-span-1">
                          {idx === 0 && <span className="text-gray-500 text-xs">Qty</span>}
                          <Input type="number" value={item.qty} onChange={e => updateSalesItem(idx, "qty", e.target.value)} className="bg-gray-800 border-gray-600 text-white h-9" />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <span className="text-gray-500 text-xs">Rate</span>}
                          <Input type="number" step="0.01" value={item.rate} onChange={e => updateSalesItem(idx, "rate", e.target.value)} className="bg-gray-800 border-gray-600 text-white h-9" />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <span className="text-gray-500 text-xs">Amount</span>}
                          <Input value={item.amount} readOnly className="bg-gray-700/50 border-gray-600 text-emerald-400 h-9 font-mono" />
                        </div>
                        <div className="col-span-1 flex justify-center">
                          {fItems.length > 1 && <Button size="sm" variant="ghost" className="text-red-400 h-9 px-2" onClick={() => setFItems(p => p.filter((_, i) => i !== idx))}><X className="w-4 h-4" /></Button>}
                        </div>
                      </div>
                      <div>
                        <Input value={item.description || ""} onChange={e => updateSalesItem(idx, "description", e.target.value)} placeholder="Description / additional notes for this item..." className="bg-gray-800/50 border-gray-700/50 text-gray-300 h-8 text-xs" />
                      </div>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setFItems(p => [...p, {...emptySalesItem}])} className="border-gray-600 text-gray-400 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Add Item
                  </Button>
                </div>
              </div>
            )}

            {/* Line Items - PURCHASE */}
            {!isSalesType && (
              <div>
                <Label className="text-gray-300 mb-2 block">Line Items (from Store Materials)</Label>
                <div className="space-y-2">
                  {fItems.map((item, idx) => (
                    <div key={idx} className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-3 space-y-2">
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-3">
                          {idx === 0 && <span className="text-gray-500 text-xs">Material</span>}
                          <PurchaseItemPicker idx={idx} item={item} />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <span className="text-gray-500 text-xs">HSN</span>}
                          <Input value={item.hsn_sac || ""} onChange={e => updateSalesItem(idx, "hsn_sac", e.target.value)} className="bg-gray-800 border-gray-600 text-white h-9 text-xs" />
                        </div>
                        <div className="col-span-1">
                          {idx === 0 && <span className="text-gray-500 text-xs">Qty</span>}
                          <Input type="number" value={item.qty} onChange={e => updateSalesItem(idx, "qty", e.target.value)} className="bg-gray-800 border-gray-600 text-white h-9" />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <span className="text-gray-500 text-xs">Rate (per kg)</span>}
                          <Input type="number" step="0.01" value={item.rate} onChange={e => updateSalesItem(idx, "rate", e.target.value)} className="bg-gray-800 border-gray-600 text-white h-9" />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <span className="text-gray-500 text-xs">Amount</span>}
                          <Input value={item.amount} readOnly className="bg-gray-700/50 border-gray-600 text-emerald-400 h-9 font-mono" />
                        </div>
                        <div className="col-span-2 flex justify-center gap-1">
                          {fItems.length > 1 && <Button size="sm" variant="ghost" className="text-red-400 h-9 px-2" onClick={() => setFItems(p => p.filter((_, i) => i !== idx))}><X className="w-4 h-4" /></Button>}
                        </div>
                      </div>
                      {/* Reel details for raw materials */}
                      {item.item_type === "raw_material" && item.material_code && (
                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <span className="text-gray-500 text-xs">Reel ID</span>
                            <Input value={item.reel_id || ""} onChange={e => updateSalesItem(idx, "reel_id", e.target.value)} placeholder="Auto" className="bg-gray-800/50 border-gray-700/50 text-cyan-400 h-8 text-xs font-mono" />
                          </div>
                          <div>
                            <span className="text-gray-500 text-xs">Reel Size (in)</span>
                            <Input type="number" step="0.01" value={item.reel_size || ""} onChange={e => updateSalesItem(idx, "reel_size", e.target.value)} className="bg-gray-800/50 border-gray-700/50 text-white h-8 text-xs" />
                          </div>
                          <div>
                            <span className="text-gray-500 text-xs">Reel Weight (kg)</span>
                            <Input type="number" step="0.01" value={item.reel_weight || ""} onChange={e => updateSalesItem(idx, "reel_weight", e.target.value)} className="bg-gray-800/50 border-gray-700/50 text-white h-8 text-xs" />
                          </div>
                          <div>
                            <span className="text-gray-500 text-xs">Type</span>
                            <Input value={item.product_type || ""} readOnly className="bg-gray-700/30 border-gray-700/50 text-gray-400 h-8 text-xs" />
                          </div>
                        </div>
                      )}
                      <Input value={item.description || ""} onChange={e => updateSalesItem(idx, "description", e.target.value)} placeholder="Description / notes..." className="bg-gray-800/50 border-gray-700/50 text-gray-300 h-8 text-xs" />
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setFItems(p => [...p, {...emptyPurchaseItem}])} className="border-gray-600 text-gray-400 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Add Item
                  </Button>
                </div>
              </div>
            )}

            {/* Tax & Totals */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-gray-300">Tax Rate</Label>
                <Select value={fTaxRate} onValueChange={setFTaxRate}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue placeholder="Select tax..." /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    <SelectItem value="0" className="text-white">No Tax (0%)</SelectItem>
                    {taxRates.map(tr => <SelectItem key={tr.id} value={String(tr.rate)} className="text-white">{tr.name} ({tr.rate}%)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-gray-300">Round Off</Label><Input type="number" step="0.01" value={fRoundOff} onChange={e => setFRoundOff(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
              <div><Label className="text-gray-300">Narration</Label><Input value={fNarration} onChange={e => setFNarration(e.target.value)} placeholder="Notes..." className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            </div>

            {/* Summary */}
            <Card className="bg-gray-800/60 border-gray-600">
              <CardContent className="p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Subtotal</span><span className="text-white font-mono">{subtotal.toFixed(2)}</span></div>
                {!isInterState && taxRate > 0 && <>
                  <div className="flex justify-between"><span className="text-gray-400">CGST ({(taxRate/2).toFixed(1)}%)</span><span className="text-white font-mono">{cgst.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">SGST ({(taxRate/2).toFixed(1)}%)</span><span className="text-white font-mono">{sgst.toFixed(2)}</span></div>
                </>}
                {isInterState && taxRate > 0 && <div className="flex justify-between"><span className="text-gray-400">IGST ({taxRate}%)</span><span className="text-white font-mono">{igst.toFixed(2)}</span></div>}
                {roundOff !== 0 && <div className="flex justify-between"><span className="text-gray-400">Round Off</span><span className="text-white font-mono">{roundOff.toFixed(2)}</span></div>}
                <div className="flex justify-between border-t border-gray-600 pt-2 text-lg">
                  <span className="text-blue-400 font-bold">Total</span>
                  <span className="text-emerald-400 font-bold font-mono">{new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(total)}</span>
                </div>
                {!isSalesType && fItems.some(i => i.material_code) && (
                  <div className="text-xs text-amber-400 mt-2 bg-amber-900/20 p-2 rounded">Auto-creates Store Inventory entries for raw materials on save</div>
                )}
              </CardContent>
            </Card>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-voucher-btn">{editing ? "Update" : "Save"} Voucher</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tally Help Dialog */}
      <Dialog open={showTallyHelp} onOpenChange={setShowTallyHelp}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-blue-400 flex items-center gap-2"><HelpCircle className="w-5 h-5" /> How to Import XML in Tally Prime</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="bg-blue-950/30 border border-blue-500/30 rounded-lg p-4">
              <p className="text-blue-300 font-medium mb-2">Ensure these ledgers exist in Tally:</p>
              <ul className="text-gray-300 space-y-1 list-disc ml-4">
                <li>Party ledgers under <span className="text-cyan-400">Sundry Debtors / Sundry Creditors</span></li>
                <li><span className="text-cyan-400">Sales Account / Purchase Account</span></li>
                <li><span className="text-cyan-400">Output/Input CGST, SGST, IGST</span> under Duties & Taxes</li>
              </ul>
            </div>
            <div className="space-y-3">
              {["Export XML from this page", "Open Tally Prime", "Press Alt + O → Import Data", "Set File Format: XML → Transactions", "Browse and select the .xml file", "Import and resolve exceptions"].map((step, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">{i+1}</span>
                  <p className="text-gray-300">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsVouchers;
